import axios from 'axios';
import { Fragment, useEffect, useRef, useState } from 'react';
import { MoonLoader } from 'react-spinners';
import { CountUp } from 'use-count-up';
import { Range } from 'react-range';
import Input from '../Input';
import Button from '../Button';
import Modal from '../Modal';
import Textarea from '../Textarea';
import { declOfNum, JSONToQuery, sleep, spaceSplitter } from '../../helpers';
import { submitClientRequest } from '../../helpers/clientRequest';
import { useLocationState } from '../../context/location';
import styles from '../../styles/modules/object/ObjectMortgage.module.scss';
import FormAgreement from '../FormAgreement';

const ObjectMortgage = ({ data, onSelect }) => {
  const locationState = useLocationState();
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSubmited, setIsSubmited] = useState(false);
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientComment, setClientComment] = useState('');
  const [agreeWithPolicy, setAgreeWithPolicy] = useState(false);
  const [initialFee, setInitialFee] = useState(Math.floor(data.price.value * 0.5));
  const [mortgageDuration, setMortgageDuration] = useState(30);
  const [minPayment, setMinPayment] = useState([
    data.mortgage?.payment || 0,
    data.mortgage?.payment || 0
  ]);
  const [mortages, setMortgages] = useState();
  const clientNameId = useRef();
  const clientPhoneId = useRef();

  const handleFormSubmit = () => {
    clientNameId.current.setError(clientName.length <= 3);
    clientPhoneId.current.setError(clientPhone.length !== 11);

    if (clientName.length > 3 && clientPhone.length === 11 && agreeWithPolicy) {
      submitClientRequest({
        clientName,
        clientPhone: clientPhone,
        clientComment,
        pathName: window.location.pathname,
        whatComponent: 'ObjectMortgage',
        location: locationState.name,
        platform: 'web'
      }).then((isSubmitted) => {
        if (isSubmitted) setIsSubmited(true);
      });
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        setIsError(false);
        await sleep(300);

        const response = await axios.get(
          process.env.NEXT_PUBLIC_API +
            `/loan${JSONToQuery({
              objectType: data.object.category,
              objectPrice: data.price.value,
              initialFee,
              mortgageDuration
            })}`,
          { signal }
        );

        if (!response.data || response.data.status !== 'ok') throw new Error('');

        setMortgages(response.data.data);
        setIsLoading(false);
        setIsError(false);
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') {
          setIsLoading(false);
          setIsError(true);
        }
      }
    };
    fetchData();

    return () => controller.abort();
  }, [initialFee, mortgageDuration]);

  useEffect(() => {
    if (!mortages?.length) return;

    const min = Math.min(...mortages.map((v) => v.payment));
    setMinPayment((prev) => [prev[1], min]);
  }, [mortages]);

  return (
    <div className={styles.mortgage} id="mortgage">
      <h3>Калькулятор ипотеки</h3>
      <Modal
        title="Заявка на консультацию"
        button={!isSubmited && 'Отправить'}
        buttonDisabled={!agreeWithPolicy}
        action={handleFormSubmit}
        visibility={isModalVisible}
        onClose={() => setIsModalVisible(false)}
      >
        {isSubmited ? (
          <Fragment>
            <div style={{ marginBottom: 18 }}>
              Спасибо за вашу заявку. Мы скоро с Вами свяжемся!
            </div>
            <img
              src="/images/thumbUp.png"
              alt=""
              style={{ padding: 50, width: '100%', boxSizing: 'border-box' }}
            />
          </Fragment>
        ) : (
          <Fragment>
            <div style={{ marginBottom: 18 }}>
              Оставьте свои контактные данные и мы с Вами свяжемся в самые кратчайшие сроки, поможем
              решить все имеющиеся у Вас вопросы!
            </div>
            <Input
              title="Имя"
              inputs={[
                {
                  placeholder: 'Василий Петрович',
                  value: clientName,
                  type: 'text'
                }
              ]}
              isWrappedStyle
              ref={clientNameId}
              onChange={(v) => setClientName(v[0])}
            />
            <Input
              title="Телефон"
              inputs={[
                {
                  placeholder: locationState.phone,
                  value: clientPhone,
                  mask: 'phone',
                  type: 'tel'
                }
              ]}
              isWrappedStyle
              ref={clientPhoneId}
              onChange={(v) => setClientPhone(v[0])}
            />
            <Textarea
              title="Коммментарий"
              placeholder="Например, «Помогите купить квартиру в ипотеку, может быть есть еще какие то варианты?»"
              autoHeight
              isWrappedStyle
              onChange={(v) => setClientComment(v)}
            />
            <FormAgreement onChange={setAgreeWithPolicy} />
          </Fragment>
        )}
      </Modal>
      <div className={styles.mortgageCalculator}>
        <div className={styles.mortgageCalculatorSettings}>
          <div className={styles.mortgageCalculatorSettingsWrapper}>
            <div className={styles.mortgageCalculatorSettingsItem}>
              <Input
                title="Стоимость объекта"
                inputs={[{ placeholder: '500 000', value: data.price.value, mask: 'money' }]}
                suffix="₽"
                style={{ marginBottom: 0 }}
                disabled
              />
            </div>
            <div className={styles.mortgageCalculatorSettingsItem}>
              <Input
                title="Первоначальный взнос"
                inputs={[
                  {
                    placeholder: '500 000',
                    value: initialFee,
                    mask: 'money',
                    max: data.price.value
                  }
                ]}
                suffix="₽"
                style={{ marginBottom: 0 }}
                onChange={(newValues) => {
                  setInitialFee(Number(newValues[0]));
                }}
              />
              <Range
                step={50000}
                min={0}
                max={data.price.value}
                values={[initialFee]}
                onChange={(values) => {
                  setInitialFee(Number(values[0]));
                }}
                renderTrack={({ props, children }) => (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      height: 0,
                      width: 'calc(100% - 32px)',
                      margin: '0 16px'
                    }}
                  >
                    {children}
                  </div>
                )}
                renderThumb={({ props }) => (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      height: 18,
                      width: 18,
                      borderRadius: '50%',
                      margin: '0 6px',
                      backgroundColor: '#2969E0',
                      outline: 'none'
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        bottom: 4,
                        left: 4
                      }}
                    />
                  </div>
                )}
              />
            </div>
            <div className={styles.mortgageCalculatorSettingsItem}>
              <Input
                title="Срок кредита"
                inputs={[{ placeholder: '10', value: mortgageDuration, mask: 'money', max: 30 }]}
                suffix={declOfNum(mortgageDuration, ['год', 'года', 'лет'])}
                style={{ marginBottom: 0 }}
                onChange={(newValues) => {
                  setMortgageDuration(newValues[0]);
                }}
              />
              <Range
                step={1}
                min={1}
                max={30}
                values={[mortgageDuration]}
                onChange={(values) => {
                  setMortgageDuration(values[0]);
                }}
                renderTrack={({ props, children }) => (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      height: 0,
                      width: 'calc(100% - 32px)',
                      margin: '0 16px'
                    }}
                  >
                    {children}
                  </div>
                )}
                renderThumb={({ props }) => (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      height: 18,
                      width: 18,
                      borderRadius: '50%',
                      margin: '0 6px',
                      backgroundColor: '#2969E0',
                      outline: 'none'
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        bottom: 4,
                        left: 4
                      }}
                    />
                  </div>
                )}
              />
            </div>
            <div className={styles.mortgageCalculatorSettingsItem} style={{ marginTop: 8 }}>
              <div className={styles.mortgageCalculatorSettingsTitle}>
                от{' '}
                <CountUp
                  isCounting
                  start={minPayment[0]}
                  end={minPayment[1]}
                  thousandsSeparator=" "
                  duration={1}
                  key={minPayment[1]}
                />{' '}
                ₽/мес.
              </div>
              <div className={styles.mortgageCalculatorSettingsSubtitle}>Минимальный платёж</div>
            </div>
          </div>
        </div>
        <div className={styles.mortgageCalculatorOptions}>
          {isLoading && (
            <div className={styles.mortgageCalculatorOptionsHelp}>
              <MoonLoader />
              <div className={styles.mortgageCalculatorOptionsText}>
                Подбираем лучшие варианты...
              </div>
            </div>
          )}
          {isError && (
            <div className={styles.mortgageCalculatorOptionsHelp}>
              <h5 style={{ marginBottom: 8 }}>Предложения не найдены</h5>
              <div style={{ opacity: 0.6, textAlign: 'center', margin: '0 18px' }}>
                Попробуйте изменить параметры калькулятора или же отправьте заявку и мы бесплатно
                подберем лучший вариант для вас
              </div>
              <Button style={{ marginTop: 12 }} onClick={() => setIsModalVisible(true)}>
                Отправить заявку
              </Button>
            </div>
          )}
          {!isLoading && !isError && (
            <Fragment>
              {mortages.slice(0, 5).map((v, i) => (
                <div
                  className={styles.mortgageCalculatorOptionsItem}
                  onClick={() => onSelect(true)}
                  key={i}
                >
                  <div className={styles.mortgageCalculatorOptionsItemIcon}>
                    <img
                      src={v.file}
                      className={styles.mortgageCalculatorOptionsItemIconImage}
                      alt=""
                    />
                  </div>
                  <div className={styles.mortgageCalculatorOptionsItemContent}>
                    <div className={styles.mortgageCalculatorOptionsItemTitle}>{v.bank}</div>
                    <div className={styles.mortgageCalculatorOptionsItemSubtitle}>
                      Ставка {v.min_percent}%
                    </div>
                  </div>
                  <div className={styles.mortgageCalculatorOptionsItemTicket}>
                    {spaceSplitter(v.payment)} ₽
                  </div>
                </div>
              ))}
              <div className={styles.mortgageCalculatorOptionsText}>
                Нажмите на необходимый вариант
              </div>
            </Fragment>
          )}
        </div>
        <div className={styles.mortgageCalculatorOptionsMobile}>
          <div
            className={styles.mortgageCalculatorOptionsMobileItems}
            style={{ marginLeft: mortages ? mortages.length * 25 - 25 + 'px' : 0 }}
          >
            {mortages &&
              mortages.map((v, i) => (
                <div
                  className={styles.mortgageCalculatorOptionsMobileItem}
                  style={{ left: i * -25 + 'px' }}
                  key={i}
                >
                  <img src={v.file} alt="" />
                </div>
              ))}
          </div>
          <Button onClick={() => onSelect(true)} fullWidth>
            Оставить заявку
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ObjectMortgage;
